/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import { ReactComponent as AscendingArrow } from 'assets/Icons/Ascending_Arrow.svg';
import { ReactComponent as DescendingArrow } from 'assets/Icons/Descending_Arrow.svg';
import { MouseEvent } from 'react';
import { flexRender, Header } from '@tanstack/react-table';
import { DetailedUsage } from 'services/usage-tracking.service';
import { QuestionHint } from 'components/shared/QuestionHint';

type Props = {
  header: Header<DetailedUsage, unknown>;
  index: number;
};

const customTooltipCss = css`
  ${tw`font-size[1rem]`}
`;

export const UsageTrackingHeader = ({ header, index }: Props) => {
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    header.getResizeHandler()?.(e);
  };

  const isSorted = header.column.getIsSorted();
  const isAsc = isSorted === 'asc';

  const handleClickSort = (e: MouseEvent<HTMLDivElement>) => {
    if (header.column.getCanSort()) {
      header.column.toggleSorting();
    }
  };

  // Check if column should be left-aligned
  const columnStyle = (header.column.columnDef as any)?.style;
  const shouldStyleCenter = columnStyle?.textAlign !== 'left';
  
  // Check if this is the compute time column
  const isComputeTimeColumn = (header.column.columnDef as any)?.accessorKey === 'computeTimeSeconds';

  return (
    <th
      style={{
        ...columnStyle,
        width: header.getSize(),
        maxWidth: header.getSize(),
        position: 'relative',
      }}
      title={!isComputeTimeColumn ? (header.column.columnDef as any)?.hint : undefined}
      tw="cursor-pointer whitespace-nowrap overflow-hidden text-overflow[ellipsis]"
    >
      <div
        onMouseDown={handleMouseDown}
        css={[resizableCss, header.column.getIsResizing() && isResizingStyle]}
      />
      <div tw="relative" onClick={(e) => handleClickSort(e)} css={headerCss}>
        <div
          tw="flex items-center space-x-3"
          css={[shouldStyleCenter && tw`justify-center`]}
        >
          {isSorted && (
            <div tw="flex flex-col space-y-2" css={[index !== 0 && tw`pl-3`]}>
              <div tw="flex items-center" css={[!isAsc && tw`opacity-40`]}>
                <DescendingArrow />
              </div>
              <div tw="flex items-center" css={[isAsc && tw`opacity-40`]}>
                <AscendingArrow />
              </div>
            </div>
          )}
          <div tw="overflow-hidden text-overflow[ellipsis] flex items-center gap-2">
            {flexRender(header.column.columnDef.header, header.getContext())}
            {isComputeTimeColumn && (
              <QuestionHint
                notTransparent
                hideCloseButton
                type={'information'}
                align={'bottomLeft'}
                action={'hover'}
                text={<div tw="py-1">Compute Time is calculated based on transcription time only.</div>}
                customStyle={customTooltipCss}
              />
            )}
          </div>
        </div>
      </div>
    </th>
  );
};

const headerCss = css`
  ${tw`hover:[> div]:(opacity-100)!`}
`;

const resizableCss = css`
  ${tw`h-1/2 absolute right-0 top-5 cursor[col-resize] select-none touch-action[none]`}
  ${tw`border-left[2px solid] border-sonnant-grey-1`}
  ${tw`hover:(border-sonnant-purple-2 border-width[3px])`}
`;

const isResizingStyle = css`
  ${tw`(opacity[1] border-width[3px] border-sonnant-purple-2)!`}
`;

